// dashboard/src/pages/apps-setup/ReposSetupTab.tsx
// Repos Setup (api lib/repo-types.ts): what each type of base repo needs from
// an app before it can build, the Unity versions its apps build on, and the
// base repositories themselves, each with its type.
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError } from '../../api/client';
import { prepareImageUpload, STORE_SCREENSHOT } from '../../lib/image-upload';
import {
  getRepoTypes, createRepoType, updateRepoType, deleteRepoType,
  addRepoTypeUnityVersion, updateRepoTypeUnityVersion, deleteRepoTypeUnityVersion, moveRepoTypeUnityVersionToBase,
  getBuildRepos, createBuildRepo, updateBuildRepo, deleteBuildRepo,
  listRepoScreenshots, uploadRepoScreenshot, deleteRepoScreenshot,
  REPO_TYPE_ITEMS, REPO_ITEM_LABELS,
  type RepoType, type RepoRule, type RepoTypeItem, type BuildRepo,
} from '../../api/app-builds';

const RULE_LABELS: Record<RepoRule, string> = { req: 'Required', opt: 'Optional', off: 'Not sent' };
const UNITY_RE = /^\d{4}\.\d+\.\d+[abfp]\d+$/;

const inputCls = 'px-2 py-1.5 text-sm rounded-md border border-border bg-surface-muted text-content';

function errText(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}

function RuleSwitch({ value, onChange, disabled, label }: {
  value: RepoRule; onChange: (v: RepoRule) => void; disabled: boolean; label: string;
}) {
  return (
    <div role="group" aria-label={label} className="inline-flex shrink-0 rounded-md border border-border overflow-hidden">
      {(['req', 'opt', 'off'] as const).map((v) => (
        <button
          key={v}
          type="button"
          aria-pressed={value === v}
          disabled={disabled}
          onClick={() => value !== v && onChange(v)}
          className={`px-2.5 py-1 text-xs border-l border-border first:border-l-0 disabled:opacity-60 ${
            value === v
              ? v === 'req' ? 'bg-primary text-white' : v === 'opt' ? 'bg-primary/15 text-primary' : 'bg-surface-muted text-content'
              : 'text-content-subtle hover:bg-surface-muted'
          }`}
        >
          {RULE_LABELS[v]}
        </button>
      ))}
    </div>
  );
}

/** The type's Unity versions: base, which ones new apps draw from, add and remove. */
function UnityVersionsOfType({ type }: { type: RepoType }) {
  const qc = useQueryClient();
  const [version, setVersion] = useState('');
  const [localError, setLocalError] = useState('');
  // The version whose removal was refused because apps of this type hold it.
  const [blocked, setBlocked] = useState<{ version: string; apps: number } | null>(null);
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['repo-types'] });
    qc.invalidateQueries({ queryKey: ['unity-versions'] });
    qc.invalidateQueries({ queryKey: ['build-config'] });
  };

  const add = useMutation({
    mutationFn: (v: string) => addRepoTypeUnityVersion(type.id, v),
    onSuccess: () => setVersion(''),
    onSettled: refresh,
  });
  const update = useMutation({
    mutationFn: ({ v, b }: { v: string; b: { base?: true; in_draw?: boolean } }) => updateRepoTypeUnityVersion(type.id, v, b),
    onSettled: refresh,
  });
  const remove = useMutation({
    mutationFn: (v: string) => deleteRepoTypeUnityVersion(type.id, v),
    onMutate: () => setBlocked(null),
    onError: (e, v) => {
      if (e instanceof ApiError && e.status === 409) setBlocked({ version: v, apps: type.unity_versions.find((u) => u.version === v)?.apps ?? 0 });
    },
    onSettled: refresh,
  });
  const move = useMutation({
    mutationFn: (v: string) => moveRepoTypeUnityVersionToBase(type.id, v),
    onSuccess: () => setBlocked(null),
    onSettled: refresh,
  });

  function submit() {
    const v = version.trim();
    setLocalError('');
    if (!UNITY_RE.test(v)) { setLocalError('Use the Unity Hub format, e.g. 6000.0.58f1.'); return; }
    if (type.unity_versions.some((u) => u.version === v)) { setLocalError(`${v} is already in the list.`); return; }
    add.mutate(v);
  }

  const error = localError || [add.error, update.error, remove.error, move.error]
    .filter((e) => e && !(e instanceof ApiError && e.status === 409 && e === remove.error))
    .map(errText)[0];

  return (
    <div className="mt-3 rounded-md bg-surface-muted px-3 py-3 space-y-2">
      {type.unity_versions.length === 0 ? (
        <p className="text-xs text-yellow-500">No versions yet. Add at least one.</p>
      ) : (
        <div className="space-y-1.5">
          {type.unity_versions.map((u) => {
            const isBase = u.version === type.unity_base;
            return (
              <div key={u.version} className="space-y-1">
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
                  <code className="text-content min-w-[7.5rem]">{u.version}</code>
                  {isBase ? (
                    <span className="px-1.5 py-0.5 rounded text-[10px] font-medium uppercase tracking-wide bg-primary/15 text-primary">base</span>
                  ) : (
                    <button
                      type="button"
                      disabled={update.isPending}
                      onClick={() => update.mutate({ v: u.version, b: { base: true } })}
                      className="text-xs text-primary hover:underline disabled:opacity-40"
                    >
                      Make base
                    </button>
                  )}
                  {isBase ? (
                    <span className="text-xs text-content-subtle">in the draw</span>
                  ) : (
                    <label className="flex items-center gap-1.5 text-xs text-content-muted" title="New apps of this type draw their version from the ones in the draw. Keep a new version out until a test build on one app has passed.">
                      <input
                        type="checkbox"
                        checked={u.in_draw}
                        disabled={update.isPending}
                        onChange={() => update.mutate({ v: u.version, b: { in_draw: !u.in_draw } })}
                      />
                      in the draw
                    </label>
                  )}
                  <span className="text-xs text-content-subtle">{u.apps} app{u.apps === 1 ? '' : 's'}</span>
                  {!isBase && (
                    <button
                      type="button"
                      aria-label={`Remove ${u.version}`}
                      disabled={remove.isPending}
                      onClick={() => remove.mutate(u.version)}
                      className="ml-auto text-content-subtle hover:text-red-500 disabled:opacity-40"
                    >
                      ×
                    </button>
                  )}
                </div>
                {blocked?.version === u.version && (
                  <div className="flex flex-wrap items-center gap-2 text-xs">
                    <span className="text-yellow-500">{blocked.apps || 'Some'} app{blocked.apps === 1 ? '' : 's'} of this type {blocked.apps === 1 ? 'is' : 'are'} on this version.</span>
                    <button
                      type="button"
                      disabled={move.isPending || !type.unity_base}
                      onClick={() => {
                        if (window.confirm(`Move these apps from Unity ${u.version} to ${type.unity_base}? They build on it from their next build.`)) move.mutate(u.version);
                      }}
                      className="text-primary hover:underline disabled:opacity-40"
                    >
                      {move.isPending ? 'Moving…' : `Move them to base (${type.unity_base})`}
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      <form className="flex flex-wrap items-center gap-2 pt-1" onSubmit={(e) => { e.preventDefault(); submit(); }}>
        <input
          value={version}
          onChange={(e) => { setVersion(e.target.value); setLocalError(''); }}
          placeholder="6000.0.58f1"
          aria-label="Unity version"
          className={`${inputCls} font-mono w-40`}
        />
        <button
          type="submit"
          disabled={!version.trim() || add.isPending}
          className="px-3 py-1.5 text-sm rounded-md border border-border text-content-muted hover:bg-surface disabled:opacity-40"
        >
          {add.isPending ? 'Adding…' : 'Add'}
        </button>
      </form>
      {error ? (
        <p className="text-xs text-red-500">{error}</p>
      ) : (
        <p className="text-xs text-content-subtle">
          New apps of this type draw one of the versions in the draw at their first build; the base is always in it.
          A new version starts out of the draw: set it on one app on the Build tab for a test build first.
          A version not yet in Settings → Unity versions is added there too.
        </p>
      )}
    </div>
  );
}

function RepoTypesCard() {
  const qc = useQueryClient();
  const typesQ = useQuery({ queryKey: ['repo-types'], queryFn: getRepoTypes });
  const [curId, setCurId] = useState<number | null>(null);
  const [newName, setNewName] = useState<string | null>(null);
  const [mode, setMode] = useState<'view' | 'rename' | 'delete'>('view');
  const [renameTo, setRenameTo] = useState('');

  const types = typesQ.data ?? [];
  const cur = types.find((t) => t.id === curId) ?? types[0] ?? null;
  useEffect(() => { setMode('view'); }, [cur?.id]);

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['repo-types'] });
    qc.invalidateQueries({ queryKey: ['build-config'] });
  };
  const create = useMutation({
    mutationFn: (name: string) => createRepoType(name),
    onSuccess: (d) => { setCurId(d.id); setNewName(null); },
    onSettled: refresh,
  });
  const setRule = useMutation({
    mutationFn: ({ key, rule }: { key: RepoTypeItem; rule: RepoRule }) => updateRepoType(cur!.id, { rules: { [key]: rule } }),
    // Show the pick at once; the refetch puts it right if the save failed.
    onMutate: ({ key, rule }) => {
      qc.setQueryData<RepoType[]>(['repo-types'], (old) => old?.map((t) => (t.id === cur!.id ? { ...t, rules: { ...t.rules, [key]: rule } } : t)));
    },
    onSettled: refresh,
  });
  const rename = useMutation({
    mutationFn: (name: string) => updateRepoType(cur!.id, { name }),
    onSuccess: () => setMode('view'),
    onSettled: () => { refresh(); qc.invalidateQueries({ queryKey: ['build-repos'] }); },
  });
  const remove = useMutation({
    mutationFn: () => deleteRepoType(cur!.id),
    onSuccess: () => { setCurId(null); setMode('view'); },
    onSettled: () => { refresh(); qc.invalidateQueries({ queryKey: ['build-repos'] }); },
  });

  const error = [create.error, setRule.error, rename.error, remove.error].find(Boolean);

  return (
    <section className="bg-surface border border-border rounded-lg">
      <div className="px-4 py-3 border-b border-border">
        <h2 className="font-medium text-content">Repo types</h2>
        <p className="mt-0.5 text-xs text-content-subtle">
          Choose what each type of repo needs from an app before it can build. Changes apply to every repo of that type from the next build.
        </p>
      </div>
      <div className="px-4 py-4 space-y-4">
        {typesQ.isLoading ? (
          <p className="text-sm text-content-subtle">Loading…</p>
        ) : typesQ.error ? (
          <p className="text-sm text-red-500">{errText(typesQ.error)}</p>
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-2">
              <label className="flex items-center gap-2 text-sm text-content-muted">
                Type
                <select
                  value={cur?.id ?? ''}
                  onChange={(e) => {
                    if (e.target.value === '__new') { setNewName(''); return; }
                    setCurId(Number(e.target.value));
                  }}
                  className={`${inputCls} min-w-[14rem]`}
                >
                  {types.map((t) => (
                    <option key={t.id} value={t.id}>{t.name} · {t.repos} repo{t.repos === 1 ? '' : 's'}</option>
                  ))}
                  <option value="__new">+ New type…</option>
                </select>
              </label>
              {newName !== null && (
                <form
                  className="flex flex-wrap items-center gap-2"
                  onSubmit={(e) => { e.preventDefault(); if (newName.trim()) create.mutate(newName.trim()); }}
                >
                  <input
                    autoFocus
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Escape') setNewName(null); }}
                    placeholder="New type name"
                    aria-label="New type name"
                    className={inputCls}
                  />
                  <button
                    type="submit"
                    disabled={!newName.trim() || create.isPending}
                    className="px-3 py-1.5 text-sm rounded-md bg-primary text-white hover:opacity-90 disabled:opacity-40"
                  >
                    {create.isPending ? 'Creating…' : 'Create'}
                  </button>
                  <button type="button" onClick={() => setNewName(null)} className="text-sm text-content-muted hover:text-content">Cancel</button>
                </form>
              )}
            </div>

            {cur ? (
              <>
                <div className="divide-y divide-border/60 rounded-md border border-border">
                  {REPO_TYPE_ITEMS.map((key) => (
                    <div key={key} className="px-3 py-2.5">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <div className="min-w-0">
                          <div className="text-sm text-content">{REPO_ITEM_LABELS[key].name}</div>
                          <div className="text-xs text-content-subtle">{REPO_ITEM_LABELS[key].sub}</div>
                        </div>
                        <RuleSwitch
                          value={cur.rules[key]}
                          disabled={setRule.isPending}
                          label={`${REPO_ITEM_LABELS[key].name} for ${cur.name}`}
                          onChange={(rule) => setRule.mutate({ key, rule })}
                        />
                      </div>
                      {key === 'unity_version' && cur.rules.unity_version !== 'off' && <UnityVersionsOfType type={cur} />}
                    </div>
                  ))}
                </div>

                <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
                  {mode === 'rename' ? (
                    <form
                      className="flex flex-1 flex-wrap items-center gap-2"
                      onSubmit={(e) => { e.preventDefault(); if (renameTo.trim()) rename.mutate(renameTo.trim()); }}
                    >
                      <input
                        autoFocus
                        value={renameTo}
                        onChange={(e) => setRenameTo(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Escape') setMode('view'); }}
                        aria-label="Type name"
                        className={`${inputCls} flex-1 min-w-[10rem]`}
                      />
                      <button type="submit" disabled={!renameTo.trim() || rename.isPending} className="text-primary hover:underline disabled:opacity-40">Save</button>
                      <button type="button" onClick={() => setMode('view')} className="text-content-muted hover:text-content">Cancel</button>
                    </form>
                  ) : mode === 'delete' ? (
                    <>
                      <span className="text-content">
                        Delete {cur.name}?{cur.repos ? ` ${cur.repos} repo${cur.repos === 1 ? '' : 's'} will need a new type, and their apps cannot build until then.` : ''}
                      </span>
                      <span className="flex gap-3">
                        <button type="button" disabled={remove.isPending} onClick={() => remove.mutate()} className="text-red-500 hover:underline disabled:opacity-40">Delete</button>
                        <button type="button" onClick={() => setMode('view')} className="text-content-muted hover:text-content">Cancel</button>
                      </span>
                    </>
                  ) : (
                    <>
                      <span className="text-content-subtle">Used by {cur.repos} repo{cur.repos === 1 ? '' : 's'}.</span>
                      <span className="flex gap-3">
                        <button type="button" onClick={() => { setRenameTo(cur.name); setMode('rename'); }} className="text-primary hover:underline">Rename</button>
                        {types.length > 1 && (
                          <button type="button" onClick={() => setMode('delete')} className="text-red-500 hover:underline">Delete type</button>
                        )}
                      </span>
                    </>
                  )}
                </div>
              </>
            ) : (
              <p className="text-sm text-content-subtle">No types yet. Make one with “+ New type…”.</p>
            )}

            {error && <p className="text-sm text-red-500">{errText(error)}</p>}

            <dl className="space-y-0.5 text-xs text-content-subtle">
              <div><dt className="inline font-medium text-content-muted">Required</dt> · the build is blocked until the app has it.</div>
              <div><dt className="inline font-medium text-content-muted">Optional</dt> · sent to CI when the app has it. The build runs without it.</div>
              <div><dt className="inline font-medium text-content-muted">Not sent</dt> · never sent to CI for this type, and not shown in Build readiness.</div>
            </dl>
          </>
        )}
      </div>
    </section>
  );
}

/**
 * Library of real gameplay screenshots of the stub game a base repo builds —
 * source frames for the store screenshots of apps created from that stub.
 * The list is only fetched once the block is expanded, so collapsed repos
 * cost no requests.
 */
function RepoScreenshots({ repoId }: { repoId: number }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const shotsQ = useQuery({
    queryKey: ['build-repo-screenshots', repoId],
    queryFn: () => listRepoScreenshots(repoId),
    enabled: open,
  });

  const upload = useMutation({
    mutationFn: async (files: File[]) => {
      for (const file of files) {
        await uploadRepoScreenshot(repoId, await prepareImageUpload(file, STORE_SCREENSHOT));
      }
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['build-repo-screenshots', repoId] }),
    onError: (e) => setUploadError(e instanceof Error ? e.message : 'Upload failed'),
  });

  const remove = useMutation({
    mutationFn: (shotId: number) => deleteRepoScreenshot(repoId, shotId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['build-repo-screenshots', repoId] }),
  });

  const shots = shotsQ.data ?? [];

  return (
    <div className="min-w-0">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="text-xs text-content-muted hover:text-content"
      >
        {open ? '▾' : '▸'} Screenshots{shotsQ.isSuccess ? ` (${shots.length})` : ''}
      </button>
      {open && (
        <div className="mt-2 space-y-2">
          {shotsQ.isLoading ? (
            <p className="text-content-subtle text-xs">Loading…</p>
          ) : shots.length === 0 ? (
            <p className="text-content-subtle text-xs">No screenshots yet.</p>
          ) : (
            <div className="grid grid-cols-[repeat(auto-fill,minmax(4rem,1fr))] gap-2">
              {shots.map((s) => (
                <div key={s.id} className="min-w-0">
                  <img src={s.url} alt="" className="w-full h-24 object-cover rounded border border-border" />
                  <button
                    type="button"
                    disabled={remove.isPending}
                    onClick={() => { if (window.confirm('Delete this screenshot?')) remove.mutate(s.id); }}
                    className="mt-1 text-xs text-content-muted hover:text-red-500 disabled:opacity-40"
                  >
                    Delete
                  </button>
                </div>
              ))}
            </div>
          )}
          <label className="inline-block text-xs text-primary hover:underline cursor-pointer">
            {upload.isPending ? 'Uploading…' : 'Upload'}
            <input
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              disabled={upload.isPending}
              onChange={(e) => {
                const files = Array.from(e.target.files ?? []);
                e.target.value = '';
                if (!files.length) return;
                setUploadError('');
                upload.mutate(files);
              }}
            />
          </label>
          {uploadError && <p className="text-red-500 text-xs">{uploadError}</p>}
          {shotsQ.error && <p className="text-red-500 text-xs">{(shotsQ.error as ApiError).message}</p>}
        </div>
      )}
    </div>
  );
}

function TypeSelect({ value, types, onChange, label, disabled }: {
  value: number | null; types: RepoType[]; onChange: (id: number) => void; label: string; disabled?: boolean;
}) {
  return (
    <select
      value={value ?? ''}
      disabled={disabled}
      aria-label={label}
      onChange={(e) => e.target.value && onChange(Number(e.target.value))}
      className={`${inputCls} ${value == null ? 'border-yellow-500/60 text-yellow-500' : ''}`}
    >
      {value == null && <option value="">Choose type</option>}
      {types.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
    </select>
  );
}

function RepoManager() {
  const qc = useQueryClient();
  const reposQ = useQuery({ queryKey: ['build-repos'], queryFn: getBuildRepos });
  const typesQ = useQuery({ queryKey: ['repo-types'], queryFn: getRepoTypes });
  const types = typesQ.data ?? [];
  const [name, setName] = useState('');
  const [owner, setOwner] = useState('');
  const [repo, setRepo] = useState('');
  const [branch, setBranch] = useState('main');
  const [typeId, setTypeId] = useState<number | null>(null);
  const [notes, setNotes] = useState('');

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['build-repos'] });
    qc.invalidateQueries({ queryKey: ['repo-types'] });
    qc.invalidateQueries({ queryKey: ['build-config'] });
  };
  const create = useMutation({
    mutationFn: () => createBuildRepo({
      name: name.trim(), owner: owner.trim(), repo: repo.trim(), type_id: typeId!,
      branch: branch.trim() || undefined, notes: notes.trim() || undefined,
    }),
    onSuccess: () => {
      setName(''); setOwner(''); setRepo(''); setBranch('main'); setNotes('');
    },
    onSettled: refresh,
  });
  const update = useMutation({
    mutationFn: ({ id, b }: { id: number; b: Partial<Omit<BuildRepo, 'id'>> }) => updateBuildRepo(id, b),
    onSettled: refresh,
  });
  const remove = useMutation({
    mutationFn: (id: number) => deleteBuildRepo(id),
    onSettled: refresh,
  });

  const canCreate = name.trim() && owner.trim() && repo.trim() && typeId != null && !create.isPending;
  const repos = reposQ.data ?? [];
  const error = [update.error, remove.error].find(Boolean);

  return (
    <section className="bg-surface border border-border rounded-lg">
      <div className="px-4 py-3 border-b border-border">
        <h2 className="font-medium text-content">Base repositories ({repos.length})</h2>
        <p className="text-content-subtle text-xs mt-0.5">
          Clients on: outside clients may build from this stub in App Factory (they see its name and notes).
        </p>
      </div>
      <div className="px-4 py-4 space-y-3">
        {reposQ.isLoading ? (
          <p className="text-content-subtle text-sm">Loading…</p>
        ) : repos.length === 0 ? (
          <p className="text-content-subtle text-sm">No repos configured.</p>
        ) : (
          <div className="space-y-2">
            {repos.map((r) => (
              <div key={r.id} className="bg-surface-muted rounded-md px-3 py-2 space-y-2">
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-content text-sm truncate">{r.name}</div>
                    <div className="text-content-subtle text-xs truncate">{r.owner}/{r.repo}@{r.branch}</div>
                    {r.notes && <div className="text-content-subtle text-xs truncate">{r.notes}</div>}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button
                      type="button"
                      disabled={update.isPending}
                      onClick={() => update.mutate({ id: r.id, b: { client_visible: r.client_visible ? 0 : 1 } })}
                      title="Outside clients may build from this stub in App Factory (they see its name and notes)"
                      className={`px-2 py-1 text-xs rounded-md border ${r.client_visible ? 'border-sky-500/40 text-sky-500' : 'border-border text-content-subtle'}`}
                    >
                      {r.client_visible ? 'clients: on' : 'clients: off'}
                    </button>
                    <button
                      type="button"
                      onClick={() => update.mutate({ id: r.id, b: { enabled: r.enabled ? 0 : 1 } })}
                      className={`px-2 py-1 text-xs rounded-md border ${r.enabled ? 'border-green-500/40 text-green-500' : 'border-border text-content-subtle'}`}
                    >
                      {r.enabled ? 'enabled' : 'disabled'}
                    </button>
                    <button
                      type="button"
                      onClick={() => { if (window.confirm(`Delete repository "${r.name}"?`)) remove.mutate(r.id); }}
                      className="text-red-500 hover:underline text-xs"
                    >
                      Delete
                    </button>
                  </div>
                </div>
                <div className="flex flex-wrap items-start justify-between gap-2 pt-2 border-t border-border/50">
                  <RepoScreenshots repoId={r.id} />
                  <label className="flex items-center gap-1.5 text-xs text-content-muted">
                    Type
                    <TypeSelect
                      value={r.type_id}
                      types={types}
                      disabled={update.isPending}
                      label={`Type of ${r.name}`}
                      onChange={(id) => update.mutate({ id: r.id, b: { type_id: id } })}
                    />
                  </label>
                </div>
              </div>
            ))}
          </div>
        )}
        {error && <p className="text-red-500 text-sm">{errText(error)}</p>}

        <form
          className="flex flex-wrap items-end gap-2 pt-3 border-t border-border/50"
          onSubmit={(e) => { e.preventDefault(); if (canCreate) create.mutate(); }}
        >
          <label className="flex flex-col gap-1 min-w-[8rem] flex-1">
            <span className="text-content-subtle text-xs">Name</span>
            <input value={name} onChange={(e) => setName(e.target.value)} className={inputCls} />
          </label>
          <label className="flex flex-col gap-1 min-w-[8rem] flex-1">
            <span className="text-content-subtle text-xs">Owner</span>
            <input value={owner} onChange={(e) => setOwner(e.target.value)} className={inputCls} />
          </label>
          <label className="flex flex-col gap-1 min-w-[8rem] flex-1">
            <span className="text-content-subtle text-xs">Repo</span>
            <input value={repo} onChange={(e) => setRepo(e.target.value)} className={inputCls} />
          </label>
          <label className="flex flex-col gap-1 min-w-[6rem]">
            <span className="text-content-subtle text-xs">Branch</span>
            <input value={branch} onChange={(e) => setBranch(e.target.value)} className={inputCls} />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-content-subtle text-xs">Type</span>
            <TypeSelect value={typeId} types={types} label="Type of the new repo" onChange={setTypeId} />
          </label>
          <label className="flex flex-col gap-1 min-w-[10rem] flex-1">
            <span className="text-content-subtle text-xs">Notes (optional)</span>
            <input value={notes} onChange={(e) => setNotes(e.target.value)} className={inputCls} />
          </label>
          <button
            type="submit"
            disabled={!canCreate}
            className="px-3 py-1.5 text-sm rounded-md bg-primary text-white hover:opacity-90 disabled:opacity-40"
          >
            {create.isPending ? 'Adding…' : 'Add'}
          </button>
        </form>
        <p className="text-xs text-content-subtle">
          Branch is what CI checks out and builds. The workflow file itself must
          sit on the repo's default branch — GitHub reads repository_dispatch
          workflows only from there, so a factory-build.yml that lives only on a
          feature branch starts no run at all.
        </p>
        {create.error && <p className="text-red-500 text-sm">{errText(create.error)}</p>}
      </div>
    </section>
  );
}

export default function ReposSetupTab() {
  return (
    <div className="space-y-4">
      <RepoTypesCard />
      <RepoManager />
    </div>
  );
}
